import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Text,
} from 'react-native';

import { FacilityPowerReadiness } from './subsections/FacilityPowerReadiness';
import {
  isFacilityPowerPersistedProps,
  type FacilityPowerProps,
  type FacilityPowerPersistedProps,
  type FacilityPowerInitialProps,
} from './useFacilityPower';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { FACILITY_POWER_ADDITIONAL_SUBTITLE } from '../../../constants/facilityPowerSectionSubtitles';
import { FACILITY_POWER_NOTES_SUBSECTION_FIELDS } from '../../../constants/requestFieldDescriptors';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import { spacing } from '../../../theme';
import {
  resolveFacilityPowerAdditionalAttachmentSectionId,
  resolveFacilityPowerAdditionalAttachmentsForStep,
  shouldShowFacilityPowerAdditionalInformation,
} from '../../../utils/facilityPowerAdditionalInformation';
import { resolveEnabledStepIds } from '../../../utils/resolveRequestFormSteps';
import { RequestFormAdditionalInformation } from '../subsections/AdditionalInformation';

const FacilityPowerInitialLayout: React.FC<FacilityPowerInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing: _isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  showFullDetails = true,
  enabledStepIds: enabledStepIdsProp,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(
      (formData as { enabledStepIds?: string[] | null }).enabledStepIds,
    );
  const storedStepIds =
    requestScope?.storedStepIds ??
    (formData as { enabledStepIds?: string[] | null }).enabledStepIds ??
    null;

  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const showAdditionalInformation =
    shouldShowFacilityPowerAdditionalInformation({
      step: 'facility-power',
      enabledStepIds,
      stage: requestStage,
    });
  const additionalAttachmentSectionId =
    resolveFacilityPowerAdditionalAttachmentSectionId('facility-power');

  return (
    <View style={facilityPowerInitialStyles.container}>
      <PersistedStepHeader
        title="Facility Power Readiness"
        uomSlot={null}
        actionsSlot={null}
      />

      <FacilityPowerReadiness
        mode="initial"
        formData={formData}
        updateFormData={updateFormData}
        requestId={requestId}
        disabled={disabled}
        isEditing={_isEditing}
        showFullDetails={showFullDetails}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        enabledStepIds={enabledStepIds}
        storedStepIds={storedStepIds}
      />

      {showAdditionalInformation ? (
        <RequestFormAdditionalInformation
          shell="formSection"
          titleSubtitle={FACILITY_POWER_ADDITIONAL_SUBTITLE}
          sectionId={additionalAttachmentSectionId}
          requestId={requestId}
          allowAttachments
          attachments={resolveFacilityPowerAdditionalAttachmentsForStep(
            'facility-power',
            attachmentsBySection,
          )}
          onAttachFiles={() => onAttachFiles?.(additionalAttachmentSectionId)}
          onDeleteAttachment={onDeleteAttachment}
          isEditing={!disabled}
          disabled={disabled}
          selectedFiles={selectedFiles[additionalAttachmentSectionId] || []}
          onFilesSelected={
            onFilesSelected
              ? files => onFilesSelected(additionalAttachmentSectionId, files)
              : undefined
          }
          isOffline={offlineMode}
          fieldId="powerAdditionalInformation"
          permissionRequestStage={requestStage}
          inputLabel="Additional Facility Power Readiness Information"
          placeholder="Enter additional power information"
          value={String(formData.powerAdditionalInformation ?? '')}
          onChangeText={value =>
            updateFormData('powerAdditionalInformation', value)
          }
          maxLength={10000}
          inputUseEditableProp={false}
          inputDisabled={disabled}
          wrapFieldInRowColumn={false}
          showFullDetails={showFullDetails}
          subsectionFields={FACILITY_POWER_NOTES_SUBSECTION_FIELDS}
          headerValues={formData as Record<string, unknown>}
        />
      ) : null}
    </View>
  );
};

const FacilityPowerPersistedLayout: React.FC<FacilityPowerPersistedProps> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  enabledStepIds: enabledStepIdsProp,
}) => {
  const requestScope = useRequestScope();
  const enabledStepIds =
    enabledStepIdsProp ??
    requestScope?.enabledStepIds ??
    resolveEnabledStepIds(request.enabledStepIds);
  const storedStepIds =
    requestScope?.storedStepIds ?? request.enabledStepIds ?? null;

  const getValue = (field: string) => {
    const fromRequest = (request as unknown as Record<string, unknown>)[field];
    const raw = isEditing ? editedData[field] ?? fromRequest : fromRequest;
    if (raw === undefined || raw === null) {
      return '';
    }
    return String(raw);
  };
  const permissionStage = String(
    request.stage ?? request.sysRequestStage ?? '',
  );
  const showAdditionalInformation =
    shouldShowFacilityPowerAdditionalInformation({
      step: 'facility-power',
      enabledStepIds,
      stage: permissionStage,
    });
  const additionalAttachmentSectionId =
    resolveFacilityPowerAdditionalAttachmentSectionId('facility-power');

  return (
    <View style={facilityPowerPersistedStyles.container}>
      <PersistedStepHeader
        title="Facility Power Readiness"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={facilityPowerPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={facilityPowerPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <FacilityPowerReadiness
        mode="persisted"
        request={request}
        isEditing={isEditing}
        editedData={editedData}
        updateEditedData={updateEditedData}
        showFullDetails={showFullDetails}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        enabledStepIds={enabledStepIds}
        storedStepIds={storedStepIds}
      />

      {showAdditionalInformation ? (
        <RequestFormAdditionalInformation
          shell="formSection"
          titleSubtitle={FACILITY_POWER_ADDITIONAL_SUBTITLE}
          sectionId={additionalAttachmentSectionId}
          requestId={request.id}
          allowAttachments
          attachments={resolveFacilityPowerAdditionalAttachmentsForStep(
            'facility-power',
            attachmentsBySection,
          )}
          onAttachFiles={() => onAttachFiles?.(additionalAttachmentSectionId)}
          onDeleteAttachment={onDeleteAttachment}
          isEditing={isEditing}
          disabled={!isEditing}
          selectedFiles={selectedFiles[additionalAttachmentSectionId] || []}
          onFilesSelected={
            onFilesSelected
              ? files => onFilesSelected(additionalAttachmentSectionId, files)
              : undefined
          }
          isOffline={offlineMode}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          fieldId="powerAdditionalInformation"
          permissionRequestStage={String(
            request.stage ?? request.sysRequestStage ?? '',
          )}
          showFullDetails
          inputLabel="Additional Facility Power Readiness Information"
          placeholder="Enter additional power information"
          value={String(getValue('powerAdditionalInformation'))}
          onChangeText={
            isEditing
              ? value => updateEditedData('powerAdditionalInformation', value)
              : () => {}
          }
          maxLength={10000}
          inputUseEditableProp
          inputEditable={isEditing}
          wrapFieldInRowColumn={false}
          subsectionFields={FACILITY_POWER_NOTES_SUBSECTION_FIELDS}
          headerValues={{
            ...(request as unknown as Record<string, unknown>),
            ...(isEditing ? editedData : {}),
          }}
        />
      ) : null}
    </View>
  );
};

const facilityPowerInitialStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

const facilityPowerPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {
    // PDF button will size itself
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});

export const FacilityPower: React.FC<FacilityPowerProps> = props =>
  isFacilityPowerPersistedProps(props) ? (
    <FacilityPowerPersistedLayout {...props} />
  ) : (
    <FacilityPowerInitialLayout {...props} />
  );

FacilityPower.displayName = 'FacilityPower';

/** @deprecated Test / caption suites — compose path only */
export const FacilityPowerReadinessStep = FacilityPowerInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const FacilityPowerInRequest = FacilityPowerPersistedLayout;
